import { useData } from "../../../runtime/provider";
import ImagePicker from "@components/ImagePicker";
import LBox from "@components/LBox";
import LButton from "@components/LButton";
import LInput from "@components/LInput";
import LScreen from "@components/LScreen";
import LText from "@components/LText";
import { router } from "expo-router";
import React, { useEffect } from "react";
import { Keyboard, Platform, ScrollView } from "react-native";
import {
  USER_ME_QUERY_KEY,
  useMe,
  useUpdateUser,
  UserMeQueryData,
} from "@hooks/trpc/useMe";
import { updateUserInputSchema } from "@libs/wire";
import { uploadImage } from "@components/Account/Profile/profile.utils";
import { z } from "zod";

import { toast } from "sonner-native";

export const LissenerProfileScreen = () => {
  const scrollViewRef = React.useRef<ScrollView>(null);
  const session = useData();
  const {
    query: { data: user },
  } = useMe();

  const updateUser = useUpdateUser();

  const [newUser, setNewUser] = React.useState({
    name: user?.name,
    image: user?.image,
  });

  const [localImage, setLocalImage] = React.useState<{
    type: any;
    uri: string;
    base64?: string | null;
    mimeType?: string | null;
  }>();

  const isValid = !!(newUser.name !== user?.name || localImage);
  const [keyboardVisible, setKeyboardVisible] = React.useState(false);

  useEffect(() => {
    const showSubscription = Keyboard.addListener(
      Platform.OS === "ios" ? "keyboardWillShow" : "keyboardDidShow",
      () => {
        setKeyboardVisible(true);
      },
    );
    const hideSubscription = Keyboard.addListener(
      Platform.OS === "ios" ? "keyboardWillHide" : "keyboardDidHide",
      () => {
        setTimeout(() => {
          setKeyboardVisible(false);
        }, 50);
      },
    );

    return () => {
      showSubscription.remove();
      hideSubscription.remove();
    };
  }, []);

  return (
    <LScreen
      enableAutomaticBottomPadding={!keyboardVisible}
      enableAutomaticTopPadding={true}
      keyboard
      pb="l1"
    >
      <ScrollView
        contentContainerStyle={{ flexGrow: 1 }}
        ref={scrollViewRef}
        onContentSizeChange={() => scrollViewRef.current?.scrollToEnd()}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        showsHorizontalScrollIndicator={false}
      >
        <LBox flexGrow={1}>
          <LBox mb="s4">
            <LBox mb="l1">
              <ImagePicker
                title=""
                avatar
                allowsEditing={false}
                imageUrl={localImage?.uri ?? user?.image}
                defaultAvatarColors={
                  localImage ? null : user?.defaultAvatarColors
                }
                onPick={({ uri, type, base64, mimeType }) => {
                  setLocalImage({
                    uri,
                    type,
                    base64,
                    mimeType,
                  });
                }}
              />
            </LBox>
          </LBox>
          <LBox mb="m1">
            <LText mb="s2" color="default">
              Nickname
            </LText>
            <LInput
              variant="profile"
              value={newUser.name}
              onChangeText={(text) => {
                setNewUser({
                  ...newUser,
                  name: text,
                });
              }}
              autoComplete="off"
              autoCorrect={false}
            />
          </LBox>
          <LBox mb="m1">
            <LText mb="s2" color="default">
              Email
            </LText>
            <LInput variant="profile" value={user?.email} editable={false} />
          </LBox>
        </LBox>
        <LBox
          flexGrow={1}
          justifyContent="flex-end"
          paddingBottom={Platform.OS === "android" ? "m1" : "zero"}
        >
          <LButton
            sentryEventName="profile_save_changes"
            variant="primary"
            textVariant="bodyM2Transparent"
            height={46}
            borderRadius="max"
            onPress={async () => {
              try {
                const newData: z.infer<typeof updateUserInputSchema> = {};
                if (newUser.name !== user?.name) {
                  newData["name"] = newUser.name;
                }
                if (localImage) {
                  newData["image"] = await uploadImage(session, {
                    uri: localImage.uri,
                    contentType: localImage.mimeType,
                  });
                }

                await updateUser.mutateAsync(newData).then(() => {
                  // Durable write-through so a profile edit persists across a
                  // kill-and-relaunch even if the next user.me refetch fails
                  // on bad internet.
                  session.cache.setQueryDataDurable<UserMeQueryData>(
                    session.queryClient,
                    USER_ME_QUERY_KEY,
                    (data) => {
                      if (!data) {
                        return undefined;
                      }

                      return {
                        ...data,
                        name: newUser.name ?? data.name,
                        email: user?.email ?? data.email,
                        image: newData.image ?? data.image,
                      };
                    },
                  );
                });
                toast.success("Profile updated");
                router.back();
              } catch (error) {
                console.log(error);
                router.back();
                toast.error("Something went wrong");
              }
            }}
            enabled={isValid}
            label="SAVE CHANGES"
          />
        </LBox>
      </ScrollView>
    </LScreen>
  );
};
